import { useEffect } from 'react';
import { FormikProvider, useFormik } from 'formik';
import { Button } from 'primereact/button';
import {
  FormEntityProps,
  FieldEntityProps,
  OddFieldOutPropsType,
  ObjStrKeyStrVal,
} from '@shared/types';
import {
  arrayChunk,
  getSmsCheckbox,
  prepareFields,
  getInitialValues,
  getOddFieldOutProps,
  handleSelectMenuSuperGlobalSortOrder,
  submitHandler,
} from '@shared/utils';
import { AccessibleSmsCheckbox, AccessibleOddFieldOutElement } from '@shared/fields';
import { LpeField } from './fields';
import getFieldValidationSchema from './fieldValidation';
import './formik.css';

const selectFieldClassNames = {
  mainField: 'FormFieldFooter selectOptionsFooter',
  error: 'errorHideFooter',
};
const textFieldClassNames = {
  mainField: 'FormFieldFooter textAndEmailFieldsFooter',
  error: 'errorHideFooter',
};
const smsCheckBoxFieldClassNames = {
  container: 'smsCheckBoxContainerFooter',
  mainField: 'FormFieldFooter smsCheckBoxFooter',
  error: 'errorMessageFooter',
  textAndErrorContainer: 'smsTextAndErrorContainerFooter',
  disclaimerTextClassName: 'smsDisclaimerFooter',
};

const sortFieldsByWeight = (fields: FieldEntityProps[]) => {
  return fields.sort((a: FieldEntityProps, b: FieldEntityProps) => {
    if (
      a.weight !== undefined &&
      a.weight !== null &&
      b.weight !== undefined &&
      b.weight !== null
    ) {
      return a.weight - b.weight;
    }
    return 0;
  });
};

/**
 * Main Form component leveraging Formik Library.
 *
 * @param props form data from db
 * @constructor
 */
const FormikForm = ({ ...props }: FormEntityProps): JSX.Element => {
  // if we have no fields we have no form...
  if (props.fields) {
    const formHandler = `${props.handlerUrl}/${props.uuid}`;

    const { selectOptions, hiddenFields, dependentSelectOptions } =
      prepareFields(props.fields);

    const { FormSchema, requiredFields } = getFieldValidationSchema({
      fields: props.fields,
    });

    const smsFieldAndType = getSmsCheckbox(props.fields);
    const fieldsNoHiddenOrSms = props.fields.filter(
      (field) => field.parameterName !== 'sms_opt_in' && !field.isHidden,
    );
    const oddFieldOutProps = getOddFieldOutProps(
      fieldsNoHiddenOrSms,
      selectOptions,
      dependentSelectOptions,
      selectFieldClassNames,
      textFieldClassNames,
    );
    const lpeFieldsProps = {
      fields: fieldsNoHiddenOrSms,
      requiredFields,
      selectFieldClassNames,
      textFieldClassNames,
      smsCheckBoxFieldClassNames,
      hiddenFields,
      dependentSelectOptions,
      selectOptions,
    };
    const chunkedFieldsArray = arrayChunk(
      sortFieldsByWeight(lpeFieldsProps.fields),
      2,
    );

    const formik = useFormik<ObjStrKeyStrVal>({
      initialValues: getInitialValues(
        props.fields,
        hiddenFields,
        selectOptions,
      ),
      validationSchema: FormSchema,
      onSubmit: async (values) =>
        submitHandler(values, hiddenFields, formHandler),
    });

    const isSmsFieldTouchedAndError =
      smsFieldAndType?.field &&
      formik.errors &&
      formik.touched &&
      !!formik.errors[smsFieldAndType.field.parameterName] &&
      !!formik.touched[smsFieldAndType.field.parameterName];

    const isOddFieldTouchedAndError =
      oddFieldOutProps &&
      formik.errors &&
      formik.touched &&
      !!formik.errors[oddFieldOutProps.field.parameterName] &&
      !!formik.touched[oddFieldOutProps.field.parameterName];

    useEffect(() => {
      return () => {
        formik.resetForm();
      };
    }, []);

    return (
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit} className={'formContainerFooter'}>
          {chunkedFieldsArray.map((items, idx) => (
            <div key={idx} className={'fieldRowFooter'}>
              {items.map((field, idx) => {
                const isTouchedAndError =
                  formik.errors && formik.touched
                    ? !!formik.errors[field.parameterName] &&
                      !!formik.touched[field.parameterName]
                    : false;

                if (field.parameterName === 'phone') {
                  Object.assign(lpeFieldsProps, { formik });
                }

                handleSelectMenuSuperGlobalSortOrder(
                  lpeFieldsProps.selectOptions,
                );

                return (
                  <div key={idx} className={'fieldDivFooter'}>
                    <LpeField
                      {...{ ...lpeFieldsProps, field, isTouchedAndError }}
                    />
                  </div>
                );
              })}
            </div>
          ))}

          {oddFieldOutProps && (
            <div className={'fieldRowFooter'}>
              <div className={'fieldDivFooter'}>
                <AccessibleOddFieldOutElement
                  {...oddFieldOutProps}
                  isTouchedAndError={isOddFieldTouchedAndError ?? false}
                />
              </div>
            </div>
          )}

          {smsFieldAndType?.field && (
            <AccessibleSmsCheckbox
              field={smsFieldAndType.field}
              smsCheckBoxFieldClassNames={smsCheckBoxFieldClassNames}
              isTouchedAndError={isSmsFieldTouchedAndError ?? false}
              showErrorMessages={true}
              idSuffix="Footer"
            />
          )}

          <Button
            label={!formik.isSubmitting ? "Let's Connect" : ''}
            loading={formik.isSubmitting}
            type={'submit'}
            className={'submitButtonFooter'}
            aria-label="Submit form"
          />
        </form>
      </FormikProvider>
    );
  }

  return <></>;
};

export default FormikForm;
